import { motion } from "framer-motion";
import { hueVars } from "../data/artists";
import SiteFooter from "./SiteFooter";
import thresholdTexture from "../assets/threshold-texture.webp";

const EASE = [0.22, 1, 0.36, 1];

const doorsWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
};
const doorItem = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE } },
};

export default function Hall({ artists, onOpen }) {
  return (
    <motion.section id="hall" data-testid="hall" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, ease: "easeOut" }}>
      <div className="hall-head">
        <motion.span
          className="kicker"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
        >
          The Hall
        </motion.span>
        <h2 className="display">
          <span className="mask-line">
            <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.25, ease: EASE }}>
              Every piece begins
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.38, ease: EASE }}>
              as someone else's world.
            </motion.span>
          </span>
        </h2>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
        >
          Each door holds one person's practice and the journey we took together to create a unique collaboration. Step through each one to discover their story.
        </motion.p>
      </div>

      <motion.div className="doors" variants={doorsWrap} initial="hidden" animate="show">
        {artists.map((a, i) => (
          <motion.button
            key={a.name}
            className="door"
            variants={doorItem}
            style={hueVars(a.hue, i * 15)}
            data-testid={`artist-door-${i + 1}`}
            aria-label={`Enter ${a.name}'s world`}
            onClick={() => onOpen(i)}
          >
            <span className="no">{String(i + 1).padStart(2, "0")}</span>
            <span className="hue-bar" aria-hidden="true" />
            {a.coverVideo ? (
              <>
                <video
                  className="door-cover door-cover-vid"
                  poster={a.cover || undefined}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
                  aria-hidden="true"
                >
                  <source src={a.coverVideo} type="video/mp4" />
                  {a.coverVideoWebm && <source src={a.coverVideoWebm} type="video/webm" />}
                </video>
                <span className="door-shade" aria-hidden="true" />
              </>
            ) : a.cover ? (
              <>
                <span
                  className="door-cover"
                  style={{
                    backgroundImage: `url(${a.cover})`,
                    backgroundSize: a.coverSize || "cover",
                    backgroundPosition: a.coverPos || "center",
                  }}
                  aria-hidden="true"
                />
                <span className="door-shade" aria-hidden="true" />
              </>
            ) : (
              <span
                className="door-texture"
                style={{
                  backgroundImage: `url(${thresholdTexture})`,
                  filter: `brightness(0.5) saturate(0.85) hue-rotate(${((a.hue - 185) + 360) % 360}deg)`,
                }}
                aria-hidden="true"
              />
            )}
            <div className="foil" aria-hidden="true" />
            <h3 className="display">{a.name}</h3>
            {a.concept && <div className="concept">{a.concept}</div>}
          </motion.button>
        ))}
      </motion.div>
      <SiteFooter />
    </motion.section>
  );
}
